import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    src: String,
    width: { type: Number, default: 140 },
    height: { type: Number, default: 140 },
    autoplay: { type: Boolean, default: true },
    loop: { type: Boolean, default: true },
    debug: { type: Boolean, default: false },
    lazy: { type: Boolean, default: true }
  }

  connect() {
    this.observer = null
    this.resizeObserver = null
    this.isVisible = false
    this.setupIntersectionObserver()
  }

  disconnect() {
    this.destroyAnimation()
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  setupIntersectionObserver() {
    // If lazy loading is disabled, check if element is visible before loading
    if (!this.lazyValue) {
      if (this.isElementVisible()) {
        this.initializeAnimation()
      } else {
        // If not visible, set up a mutation observer to watch for visibility changes
        this.setupVisibilityObserver()
      }
      return
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers - load immediately if visible
      if (this.isElementVisible()) {
        this.initializeAnimation()
      }
      return
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isVisible) {
          this.isVisible = true
          this.initializeAnimation()
          // Stop observing once loaded
          this.observer.unobserve(this.element)
        }
      })
    }, {
      threshold: 0.1 // Load when 10% visible
    })

    this.observer.observe(this.element)
  }

  isElementVisible() {
    const style = window.getComputedStyle(this.element)
    return style.display !== 'none' && style.visibility !== 'hidden' && !this.element.classList.contains('d-none')
  }

  setupVisibilityObserver() {
    if (!('MutationObserver' in window)) {
      return
    }

    this.visibilityObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'class' || mutation.attributeName === 'style')) {
          if (this.isElementVisible() && !this.isVisible) {
            this.isVisible = true
            this.initializeAnimation()
            this.visibilityObserver.disconnect()
          }
        }
      })
    })

    this.visibilityObserver.observe(this.element, {
      attributes: true,
      attributeFilter: ['class', 'style']
    })
  }

  setupResizeObserver() {
    if (!('ResizeObserver' in window)) {
      return
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (this.dotLottieInstance && entry.target === this.element) {
          this.resizeAnimation()
        }
      }
    })

    this.resizeObserver.observe(this.element)
  }

  resizeAnimation() {
    if (!this.dotLottieInstance) return

    const canvas = this.element.querySelector('canvas')
    if (!canvas) return

    const containerWidth = this.element.offsetWidth
    const containerHeight = this.element.offsetHeight
    const size = Math.min(containerWidth, containerHeight, this.widthValue, this.heightValue)

    if (canvas.width !== size || canvas.height !== size) {
      canvas.width = size
      canvas.height = size
      // Just resize the canvas, don't recreate the animation
      if (this.dotLottieInstance && this.dotLottieInstance.resize) {
        this.dotLottieInstance.resize()
      }
    }
  }

  async initializeAnimation() {
    if (!window.DotLottie) {
      console.error('DotLottie not available')
      return
    }

    if (this.dotLottieInstance) {
      return // Already initialized
    }

    try {
      // Clear existing content
      this.element.innerHTML = ''

      // Create canvas element
      const canvas = document.createElement('canvas')
      canvas.width = this.widthValue
      canvas.height = this.heightValue
      
      if (this.debugValue) {
        canvas.style.border = '1px solid red'
      }
      this.element.appendChild(canvas)

      // Create DotLottie instance
      this.dotLottieInstance = new window.DotLottie({
        canvas: canvas,
        src: this.srcValue,
        autoplay: this.autoplayValue,
        loop: this.loopValue
      })

      // Add event listeners (only for debugging if needed)
      if (this.debugValue) {
        this.dotLottieInstance.addEventListener('error', (error) => {
          console.error(`Animation error in ${this.element.id}:`, error)
        })
      }


    } catch (error) {
      console.error(`Error creating Lottie animation in ${this.element.id}:`, error)
    }
  }

  destroyAnimation() {
    if (this.dotLottieInstance) {
      try {
        this.dotLottieInstance.destroy()
        this.dotLottieInstance = null
      } catch (error) {
        console.error(`Error destroying animation in ${this.element.id}:`, error)
      }
    }
  }

  // Public methods for external control
  async play() {
    if (this.dotLottieInstance) {
      await this.dotLottieInstance.play()
    }
  }

  async pause() {
    if (this.dotLottieInstance) {
      await this.dotLottieInstance.pause()
    }
  }

  async stop() {
    if (this.dotLottieInstance) {
      await this.dotLottieInstance.stop()
    }
  }

  async setSpeed(speed) {
    if (this.dotLottieInstance) {
      await this.dotLottieInstance.setSpeed(speed)
    }
  }

  // Action methods for HTML triggers
  playAnimation() {
    this.play()
  }

  pauseAnimation() {
    this.pause()
  }

  stopAnimation() {
    this.stop()
  }
}
